<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Button, Textarea } from '@drama/ui'
import FormField from '../NovelFormField.vue'
import NovelIssueNote from '../NovelIssueNote.vue'
import { uniqueId } from '@drama/novel-authoring'
import { useSectionModel } from './section-model.js'
import './novel-sections.css'

const props = defineProps({
  modelValue: { type: Object, required: true },
  issues: { type: Array, default: () => [] },
  upload: { type: Function, default: null },
})

const emit = defineEmits(['update:modelValue', 'blocked'])
const { t } = useI18n()
const { bind, invalid, notes, removeEntity, toggle, write, error } = useSectionModel(props, emit)
const facts = computed(() => props.modelValue.facts ?? [])
const people = computed(() => props.modelValue.characters ?? [])
const personIds = computed(() => people.value.map(person => person.id))

function addFact() {
  write(['facts'], [...facts.value, { id: uniqueId('fact', facts.value), text: '', observer_ids: [props.modelValue.player_id], disclosable: false }])
}
</script>

<template>
  <section
    class="novel-entities"
    data-testid="novel-facts-section"
  >
    <header class="novel-entities__header">
      <p>{{ t('novelStudio.fields.count', { count: facts.length }) }}</p>
      <Button
        variant="secondary"
        size="sm"
        data-testid="novel-add-fact"
        @click="addFact"
      >
        {{ t('novelStudio.fields.addFact') }}
      </Button>
    </header>
    <div
      class="novel-entity__help"
      data-testid="novel-facts-help"
    >
      <p>{{ t('novelStudio.fields.factsHint') }}</p>
      <details>
        <summary>{{ t('novelStudio.fields.writingExampleLabel') }}</summary>
        <p>{{ t('novelStudio.fields.factsExample') }}</p>
      </details>
    </div>
    <p class="novel-entities__hint">
      {{ t('novelStudio.fields.referenceHelp') }}
    </p>
    <p
      v-if="!facts.length"
      class="novel-entities__empty"
    >
      {{ t('novelStudio.empty.facts') }}
    </p>
    <article
      v-for="(fact, index) in facts"
      :key="`${fact.id}-${index}`"
      class="novel-entity"
      :class="{ 'has-error': invalid(['facts', index]) }"
      :data-path="`facts.${index}`"
      tabindex="-1"
      :data-testid="`novel-fact-${index}`"
    >
      <header class="novel-entity__header">
        <div class="novel-entity__title">
          <code>{{ fact.id }}</code>
        </div>
        <Button
          variant="secondary"
          size="sm"
          :aria-label="t('novelStudio.fields.removeNamed', { name: fact.id })"
          :data-testid="`novel-fact-${index}-remove`"
          @click="removeEntity('facts', index, fact.id)"
        >
          {{ t('novelStudio.fields.remove') }}
        </Button>
      </header>
      <NovelIssueNote :notes="notes(['facts', index])" />
      <FormField
        :error="error(['facts', index, 'text'])"
        required
        :label="t('novelStudio.fields.factText')"
      >
        <Textarea
          v-bind="bind(['facts', index, 'text'])"
          :rows="3"
        />
      </FormField>
      <fieldset
        class="novel-entity__checks"
        :data-path="`facts.${index}.observer_ids`"
      >
        <legend>{{ t('novelStudio.requirements.requiredLabel', { label: t('novelStudio.fields.observers') }) }}</legend>
        <label
          v-for="person in people"
          :key="person.id"
          class="novel-entity__check"
        >
          <input
            type="checkbox"
            :checked="(fact.observer_ids || []).includes(person.id)"
            @change="toggle(['facts', index, 'observer_ids'], person.id, $event.target.checked, personIds)"
          >
          {{ person.name || person.id }}
        </label>
      </fieldset>
      <p class="novel-entities__hint">
        {{ t('novelStudio.fields.observersHint') }}
      </p>
      <label
        v-if="Object.hasOwn(fact, 'disclosable')"
        class="novel-entity__check"
        :data-path="`facts.${index}.disclosable`"
      >
        <input
          type="checkbox"
          :checked="fact.disclosable === true"
          @change="write(['facts', index, 'disclosable'], $event.target.checked)"
        >
        {{ t('novelStudio.fields.disclosable') }}
      </label>
      <p
        v-if="Object.hasOwn(fact, 'disclosable')"
        class="novel-entities__hint"
      >
        {{ t('novelStudio.fields.disclosableHint') }}
      </p>
    </article>
  </section>
</template>
